import React from 'react';
import { Button, Space, Typography } from 'antd';
import { isBannedAmbiguousPrimaryLabel } from '../actionGrammar';
import {
  PRIMITIVE_AUTHORITY_FLAGS,
  type PrimitivePresentationState,
} from './primitiveSurfaceState';
import { PrimitiveSurfaceFrame } from './PrimitiveSurfaceFrame';

const { Text } = Typography;

export interface SecondaryActionEntryV0 {
  readonly key: string;
  readonly label: string;
  readonly href?: string | null;
  readonly onSelect?: () => void;
  readonly disabled?: boolean;
}

export interface SecondaryActionGroupProps {
  readonly presentationState: PrimitivePresentationState;
  readonly actions?: readonly SecondaryActionEntryV0[];
  readonly grantsAuthority?: false;
  readonly grantsApproval?: false;
  readonly 'data-testid'?: string;
}

/**
 * SecondaryActionGroup (Family B) — hosts non-primary actions (navigate to
 * log, open related object, copy reference, etc.). Structurally distinct from
 * PrimaryActionArea: every entry renders with Ant `type="default"` only, so a
 * secondary action can never visually compete with — or be mistaken for — the
 * page's primary/decision actions.
 */
export function SecondaryActionGroup(props: SecondaryActionGroupProps): React.ReactElement {
  const grantsAuthority = props.grantsAuthority ?? PRIMITIVE_AUTHORITY_FLAGS.grantsAuthority;
  const grantsApproval = props.grantsApproval ?? PRIMITIVE_AUTHORITY_FLAGS.grantsApproval;
  if (grantsAuthority !== false || grantsApproval !== false) {
    throw new Error('SecondaryActionGroup must keep grantsAuthority/grantsApproval=false');
  }

  const actions = props.actions ?? [];

  return (
    <PrimitiveSurfaceFrame
      title="보조 작업"
      presentationState={props.presentationState}
      emptyMessage="사용 가능한 보조 작업이 없습니다."
      data-testid={props['data-testid'] ?? 'secondary-action-group'}
      aria-label="보조 작업"
    >
      {actions.length === 0 ? (
        <Text type="secondary">보조 작업이 없습니다.</Text>
      ) : (
        <Space wrap data-testid="secondary-action-group-list">
          {actions.map((action) => {
            if (isBannedAmbiguousPrimaryLabel(action.label)) {
              throw new Error(`SecondaryActionGroup banned label: ${action.label}`);
            }
            return (
              <Button
                key={action.key}
                type="default"
                href={action.href ?? undefined}
                disabled={action.disabled}
                onClick={action.onSelect}
                data-testid={`secondary-action-${action.key}`}
              >
                {action.label}
              </Button>
            );
          })}
        </Space>
      )}
    </PrimitiveSurfaceFrame>
  );
}
